import { useEffect, useState } from "react";

/**
 * Client-side ETA fallback — derives a rough estimate from elapsed time and
 * progress when the backend provides no `etaSeconds` (first-ever run for a
 * user/jobKind, or backend history table is empty).
 *
 * Waits until at least 2 items are done so the extrapolation is not wildly
 * inaccurate from a single fast/slow outlier. Updates every 3 s to stay
 * responsive without excessive re-renders.
 *
 * Returns `null` when there is not enough data to extrapolate.
 */
export default function useClientEta(done, total, startedAt, running) {
  const [eta, setEta] = useState(null);

  useEffect(() => {
    if (!running || !startedAt || total <= 0 || done < 2) {
      setEta(null);
      return undefined;
    }

    function compute() {
      const elapsedMs = Date.now() - startedAt;
      if (elapsedMs <= 0) return null;
      const remaining = total - done;
      if (remaining <= 0) return 0;
      const msPerItem = elapsedMs / done;
      return Math.max(0, Math.round((msPerItem * remaining) / 1000));
    }

    setEta(compute());
    const id = window.setInterval(() => setEta(compute()), 3000);
    return () => window.clearInterval(id);
  }, [done, total, startedAt, running]);

  return eta;
}
